import { useState, type ReactNode } from 'react';
import { CornerDownRight, MessageCircleQuestion } from 'lucide-react';
import {
  answerFor,
  answersComplete,
  formatFactoryAnswers,
  TEXT_MAX,
  formatQuestionsReply,
  QUESTIONS_REPLY_MAX,
  type FactoryAnswer,
  type FactoryQuestion,
  type FactoryQuestions,
} from '@shared/factory-questions';
import { TASK_CHAT_MAX } from '@shared/types/tasks';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useTaskDraft } from './task-drafts';
import { ChoiceCardGroup } from '@/components/ChoiceCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FoldingCard } from './FoldingCard';

/**
 * T-55: the software factory's questions on a task (a `factory-questions` block in its comment), as
 * a card, copied from the factory's own so they look and answer the same. While they're open, anyone
 * who can comment picks options or writes an answer and sends them as one comment (the factory's
 * Answers format), which the factory reads back. Once answered, or without `onAnswer`, it shows the
 * questions and their options, or with `answers` each question's answer. A plain comment doesn't
 * close it. Reply in words opens a box for one written reply in place of an answer per question
 * (formatQuestionsReply), which closes the round like Send answers; once answered, `words` shows it.
 */
export function FactoryQuestionsCard({
  questions,
  answered = false,
  answers: given,
  words,
  meta,
  from = 'the AI builder',
  fold = false,
  id,
  onAnswer,
}: {
  questions: FactoryQuestions;
  /** Who asked, in the header. */
  from?: string;
  /** A person has replied since (the timeline knows): the questions show as asked, not to answer. */
  answered?: boolean;
  /** Once answered: one answer per question, in order (parseFactoryAnswers), shown in place of the options. */
  answers?: (string | null)[] | null;
  /** Once answered with a reply in words, its text, shown after the questions. */
  words?: string | null;
  /** Who asked and when, at the end of the header line (the task's Decisions). */
  meta?: ReactNode;
  /** Starts folded to its header line, which opens it (an answered round in the task's Decisions). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it (decisionRoundId). */
  id?: string;
  /** Posts the answers (or a reply in words) as a comment; leave out where nobody can answer (a preview). */
  onAnswer?: (message: string) => void;
}) {
  const [typed, setAnswers] = useState<Record<string, FactoryAnswer>>({});
  // The Reply in words box; null while it's closed.
  const [reply, setReply] = useState<string | null>(null);
  const open = !answered && Boolean(onAnswer);
  const answers: Record<string, FactoryAnswer | undefined> = typed;
  const set = (id: string, change: Partial<FactoryAnswer>) =>
    setAnswers((prev) => {
      const was = answerFor(prev, id) ?? { picked: [], other: '' };
      return { ...prev, [id]: { ...was, ...change } };
    });
  const complete = answersComplete(questions.questions, answers);
  const message = formatFactoryAnswers(questions.questions, answers);
  // One comment holds them all: past the limit the server would cut it short, so it isn't sent.
  const tooLong = message.length > TASK_CHAT_MAX;
  // Picked or written and not sent: leaving the page or closing the task asks first.
  const dirty =
    open && (reply?.trim() ? true : Object.values(typed).some((a) => Boolean(a && (a.picked.length || a.other.trim()))));
  useUnsavedChangesGuard(dirty, 'Your answers to the questions aren’t sent yet. Leave without sending them?');
  useTaskDraft(dirty);

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={given ? answersSummary(questions.questions, given) : words ? `Replied: ${words.replace(/\s+/g, ' ')}` : undefined}
      header={
        <>
          <MessageCircleQuestion className="size-4" aria-hidden />
          {questions.questions.length === 1 ? `A question from ${from}` : `${questions.questions.length} questions from ${from}`}
          {answered ? <Badge variant="secondary">Answered</Badge> : null}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      <ol className="space-y-3">
        {questions.questions.map((q, i) => (
          <li key={q.id} className="space-y-1.5">
            <p className="text-sm">
              <span className="text-muted-foreground">{i + 1}. </span>
              {q.header ? <span className="font-medium">{q.header}: </span> : null}
              {q.question}
            </p>
            {open && reply === null ? (
              <QuestionInput question={q} answer={answerFor(answers, q.id)} onChange={(change) => set(q.id, change)} />
            ) : given ? (
              <GivenAnswer answer={given[i] ?? null} />
            ) : (
              <OptionList question={q} />
            )}
          </li>
        ))}
      </ol>
      {words && !given ? (
        <div className="space-y-1 border-t pt-2">
          <p className="text-xs text-muted-foreground">Replied in words</p>
          <GivenAnswer answer={words} />
        </div>
      ) : null}
      {open && reply !== null ? (
        <div className="space-y-2">
          <Textarea
            aria-label="Your reply to the questions"
            placeholder="Answer or skip the questions in your own words…"
            rows={3}
            maxLength={QUESTIONS_REPLY_MAX}
            autoFocus
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setReply(null)}>
              Back to the questions
            </Button>
            <Button size="sm" disabled={!reply.trim()} onClick={() => onAnswer?.(formatQuestionsReply(reply))}>
              Send reply
            </Button>
          </div>
        </div>
      ) : open ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setReply('')}>
            Reply in words
          </Button>
          {!complete ? (
            <span className="text-xs text-muted-foreground">Answer each question to send</span>
          ) : tooLong ? (
            <span className="text-xs text-destructive">Too long for one comment: shorten your answers</span>
          ) : null}
          <Button size="sm" disabled={!complete || tooLong} onClick={() => onAnswer?.(message)}>
            Send answers
          </Button>
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** The answers on one line for a folded round: "Scope: Only tasks it built · Where: On the task". */
function answersSummary(list: FactoryQuestion[], given: (string | null)[]): string | undefined {
  const parts = list.flatMap((q, i) => {
    const answer = given[i]?.replace(/\s*\(Recommended\)/g, '').trim();
    return answer ? [q.header ? `${q.header}: ${answer}` : answer] : [];
  });
  return parts.length ? parts.join(' · ') : undefined;
}

function GivenAnswer({ answer }: { answer: string | null }) {
  return (
    <p className={cn('flex items-start gap-1.5 pl-4 text-sm', answer ? 'font-medium' : 'text-muted-foreground')}>
      <CornerDownRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 whitespace-pre-line break-words">{answer ?? 'No answer'}</span>
    </p>
  );
}

/**
 * In the comments, the factory's questions as one line pointing to the task's Decisions (under the
 * description), where they're answered and kept with their answers.
 */
export function FactoryQuestionsPointer({
  count,
  answered = false,
  onShow,
}: {
  count: number;
  answered?: boolean;
  /** Scrolls to the questions (showFoldingCard). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <MessageCircleQuestion className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Asked a question' : `Asked ${count} questions`}</span>
      {answered ? <Badge variant="secondary">Answered</Badge> : null}
      <Button variant="link" size="inline" onClick={onShow}>
        {answered ? 'See Decisions' : 'Answer under the description'}
      </Button>
    </div>
  );
}

function OptionList({ question }: { question: FactoryQuestion }) {
  if (!question.options.length) return null;
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground marker:text-muted-foreground">
      {question.options.map((o) => (
        <li key={o.label}>
          <span className="text-foreground">{o.label}</span>
          {o.description ? ` — ${o.description}` : null}
        </li>
      ))}
    </ul>
  );
}

function QuestionInput({
  question,
  answer,
  onChange,
}: {
  question: FactoryQuestion;
  answer: FactoryAnswer | undefined;
  onChange: (change: Partial<FactoryAnswer>) => void;
}) {
  const picked = answer?.picked ?? [];
  const label = question.header ?? question.question;
  return (
    <div className="space-y-2">
      {question.options.length && question.multiSelect ? (
        <div role="group" aria-label={label} className="space-y-1.5">
          {question.options.map((o) => (
            <label key={o.label} className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={picked.includes(o.label)}
                onCheckedChange={(on) =>
                  onChange({ picked: on === true ? [...picked, o.label] : picked.filter((p) => p !== o.label) })
                }
              />
              <span className="min-w-0">
                {o.label}
                {o.description ? <span className="block text-xs text-muted-foreground">{o.description}</span> : null}
              </span>
            </label>
          ))}
        </div>
      ) : question.options.length ? (
        <ChoiceCardGroup
          size="sm"
          label={label}
          value={picked[0] ?? ''}
          onValueChange={(value) => onChange({ picked: [value] })}
          options={question.options.map((o) => ({ value: o.label, title: o.label, description: o.description }))}
        />
      ) : null}
      <Input
        aria-label={`${label}: ${question.options.length ? 'another answer' : 'your answer'}`}
        placeholder={question.options.length ? 'Or write your own answer…' : 'Your answer…'}
        maxLength={TEXT_MAX}
        value={answer?.other ?? ''}
        onChange={(e) => onChange({ other: e.target.value })}
      />
    </div>
  );
}
